import {
  computed,
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  Renderer2,
  TemplateRef,
  untracked,
  ViewContainerRef,
} from '@angular/core'
import { generateUuid } from '@ftr/contracts/type/shared'
import { toHTMLElement } from '../util'
import { DrawerContext } from './drawer.component'
import { DrawerPosition, DrawerService } from './drawer.service'

/**
 * A directive used to display content in a slide-in overlay.
 *
 * The element this directive is applied to becomes the trigger element that toggles the drawer.
 * The drawer includes a backdrop that can be clicked to close, and supports ESC to close.
 *
 * For more programmatic control of drawers, use the {@link DrawerService} directly.
 *
 * @example
 * ``` html
 * <button
 *   ftrDrawer
 *   [drawerContent]="myDrawer"
 *   drawerPosition="right"
 * >
 *   Open drawer
 * </button>
 * <ng-template #myDrawer let-drawerContext>
 *   <div class="drawer-content">
 *     <h2>Drawer Title</h2>
 *     <hr />
 *     <button (click)="drawerContext.close()">Close</button>
 *   </div>
 * </ng-template>
 * ```
 */
@Directive({
  selector: '[ftrDrawer]',
  exportAs: 'ftrDrawer',
})
export class DrawerDirective implements OnInit, OnDestroy {
  private readonly triggerRef = inject<ElementRef<HTMLElement>>(ElementRef)
  private readonly renderer = inject(Renderer2)
  private readonly drawerService = inject(DrawerService)
  private readonly viewContainerRef = inject(ViewContainerRef)

  readonly drawerId = `ftr-drawer-${generateUuid()}`
  private readonly triggerElement = toHTMLElement(this.triggerRef)
  private triggerEventsCleanup: (() => void)[] = []
  private isInitialised = false

  /** The template to render inside the drawer overlay. */
  readonly drawerContent = input.required<TemplateRef<DrawerContext>>()

  /** The side of the screen from which the drawer enters. */
  readonly drawerPosition = input.required<DrawerPosition>()

  readonly drawerOpenChanged = output<boolean>()

  private readonly isOpen = computed<boolean>(() => this.drawerService.currentDrawerId() === this.drawerId)

  ngOnInit(): void {
    this.setUpTriggerAttributes()
    this.setUpTriggerEvents()
  }

  ngOnDestroy(): void {
    this.removeTriggerEvents()

    if (this.isOpen()) {
      this.drawerService.close()
    }
  }

  openDrawer(): void {
    this.drawerService.open({
      content: this.drawerContent(),
      position: this.drawerPosition(),
      viewContainerRef: this.viewContainerRef,
      drawerId: this.drawerId,
    })
  }

  closeDrawer(): void {
    this.drawerService.close()
  }

  toggleDrawer(): void {
    this.isOpen() ? this.closeDrawer() : this.openDrawer()
  }

  private setUpTriggerAttributes(): void {
    this.renderer.setAttribute(this.triggerElement, 'aria-haspopup', 'dialog')
    this.renderer.setAttribute(this.triggerElement, 'aria-expanded', String(this.isOpen()))
    this.renderer.setAttribute(this.triggerElement, 'aria-controls', this.drawerId)
  }

  private setUpTriggerEvents(): void {
    const click = this.renderer.listen(this.triggerElement, 'click', () => this.toggleDrawer())
    this.triggerEventsCleanup = [click]
  }

  private removeTriggerEvents(): void {
    this.triggerEventsCleanup.forEach(cleanup => cleanup())
    this.triggerEventsCleanup = []
  }

  private openStateEffect = effect(() => {
    const isOpen = this.isOpen()

    untracked(() => {
      this.renderer.setAttribute(this.triggerElement, 'aria-expanded', String(isOpen))

      if (this.isInitialised) {
        this.drawerOpenChanged.emit(isOpen)
      } else {
        this.isInitialised = true
      }
    })
  })
}
